/**
 * Direct browser → Tigris MP4 upload (presigned PUT, no FastAPI body proxy).
 *
 * Flow:
 *   1. POST /videos/upload/initiate  → presigned PUT URL (15-min TTL).
 *   2. XHR PUT the MP4 straight to Tigris, with the exact `Content-Type` the
 *      signature was generated against. Progress + speed + ETA are reported
 *      via onStats. Caller can abort via an AbortSignal.
 *   3. POST /videos/upload/complete  → backend HEADs the object, verifies
 *      size + MIME, flips the staged DB row to `ready`, returns the same
 *      `{file_id, storage_key, ...}` handle as the legacy multipart route.
 *
 * Why direct uploads:
 *   - Bypasses Nginx body-size cap on the edge proxy.
 *   - Bypasses uvicorn's request body buffer.
 *   - Halves wire time on production (one hop vs three).
 *
 * Caller wiring:
 *   const ctrl = new AbortController();
 *   const handle = await uploadVideoDirect({
 *     file, api,
 *     signal: ctrl.signal,
 *     onPhase: (msg) => setPhase(msg),
 *     onStats: ({percent, loadedBytes, totalBytes, speedBps, etaSeconds}) => { ... },
 *   });
 *   // To cancel: ctrl.abort();
 */
const ALLOWED_EXT = '.mp4';
const ALLOWED_MIME = 'video/mp4';

/** Errors thrown when the user cancels carry this flag so callers can swallow
 *  them without surfacing a scary toast. */
export class UploadCancelledError extends Error {
  constructor() {
    super('Upload cancelled');
    this.name = 'UploadCancelledError';
    this.cancelled = true;
  }
}

/** Compute a smoothed transfer rate from a rolling sample window.
 *  Single-sample bps is jittery on Chrome (events fire in bursts), so we
 *  average the last ~3s of progress events for a number that feels stable
 *  enough to drive an ETA without thrashing.
 *  Returns { speedBps, etaSeconds } or { speedBps: 0, etaSeconds: null }
 *  when we don't yet have enough signal. */
function makeSpeedTracker(totalBytes) {
  const samples = []; // { t: ms, loaded: bytes }
  const WINDOW_MS = 3000;
  return (loaded) => {
    const now = performance.now();
    samples.push({ t: now, loaded });
    // Drop samples older than the window
    while (samples.length > 1 && now - samples[0].t > WINDOW_MS) samples.shift();
    if (samples.length < 2) return { speedBps: 0, etaSeconds: null };
    const first = samples[0];
    const last = samples[samples.length - 1];
    const dt = (last.t - first.t) / 1000;
    if (dt <= 0) return { speedBps: 0, etaSeconds: null };
    const speedBps = (last.loaded - first.loaded) / dt;
    if (speedBps <= 0) return { speedBps: 0, etaSeconds: null };
    const remaining = Math.max(0, totalBytes - loaded);
    return { speedBps, etaSeconds: remaining / speedBps };
  };
}

/** Upload an MP4 directly to Tigris via a backend-issued presigned URL.
 *  Returns the `{file_id, storage_key, size_bytes, content_type, filename}`
 *  handle from /upload/complete on success.
 *
 *  @param {Object}   opts
 *  @param {File}     opts.file       — the MP4 file to upload
 *  @param {Object}   opts.api        — useApi() instance (for /initiate, /complete)
 *  @param {AbortSignal} [opts.signal] — abort the XHR mid-flight
 *  @param {(msg:string)=>void} [opts.onPhase] — coarse phase label for UI
 *  @param {(stats:{percent:number, loadedBytes:number, totalBytes:number,
 *           speedBps:number, etaSeconds:number|null}) => void} [opts.onStats]
 *           — fine-grained progress, called on every XHR progress event
 */
export async function uploadVideoDirect({ file, api, signal, onPhase, onStats }) {
  if (!file) throw new Error('No file selected.');
  if (!file.name.toLowerCase().endsWith(ALLOWED_EXT)) {
    throw new Error('Only MP4 files are supported.');
  }
  if (signal?.aborted) throw new UploadCancelledError();

  onPhase?.('Preparing upload…');
  const initiate = await api.post('/videos/upload/initiate', {
    filename: file.name,
    size_bytes: file.size,
    content_type: ALLOWED_MIME,
  });

  // Sanity: backend should have echoed back our content-type contract. If a
  // future backend change forgets to include this header, fall back to the
  // one we know we asked for.
  const ct = initiate.upload_headers?.['Content-Type'] || ALLOWED_MIME;

  if (signal?.aborted) throw new UploadCancelledError();

  onPhase?.('Uploading to storage…');
  await new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    const trackSpeed = makeSpeedTracker(file.size);

    xhr.open('PUT', initiate.upload_url);
    // Tigris signed against this exact Content-Type. Mismatch → 403
    // SignatureDoesNotMatch. No Authorization header — the signature is in
    // the URL query string. No `withCredentials` — cross-origin to Tigris.
    xhr.setRequestHeader('Content-Type', ct);

    // Wire up the abort signal: when the caller aborts, kill the XHR.
    // `xhr.abort()` triggers `onabort`, which we route into reject with the
    // cancel-flagged error so the caller can swallow it cleanly.
    const onAbort = () => xhr.abort();
    if (signal) signal.addEventListener('abort', onAbort, { once: true });
    const detachSignal = () => signal?.removeEventListener('abort', onAbort);

    xhr.upload.onprogress = (e) => {
      if (!e.lengthComputable) return;
      const percent = Math.round((e.loaded / e.total) * 100);
      const { speedBps, etaSeconds } = trackSpeed(e.loaded);
      onStats?.({
        percent,
        loadedBytes: e.loaded,
        totalBytes: e.total,
        speedBps,
        etaSeconds,
      });
    };
    xhr.onload = () => {
      detachSignal();
      if (xhr.status >= 200 && xhr.status < 300) {
        resolve();
      } else if (xhr.status === 0) {
        // No HTTP response — almost always a CORS preflight failure against
        // Tigris (browser blocks the PUT before it goes on the wire). Surface
        // an actionable message so ops can fix the bucket CORS policy.
        console.error('[video-upload-direct] xhr.status=0 (CORS or network)', {
          name: file.name, size: file.size, url: initiate.upload_url,
        });
        reject(new Error(
          'Upload blocked by storage CORS policy. Ask an admin to allow PUT ' +
          'from this origin on the video storage bucket.',
        ));
      } else {
        console.error('[video-upload-direct] non-2xx from storage', {
          status: xhr.status, response: xhr.responseText?.slice(0, 500),
          name: file.name, size: file.size,
        });
        reject(new Error(`Storage upload failed (${xhr.status}).`));
      }
    };
    xhr.onerror = () => {
      detachSignal();
      console.error('[video-upload-direct] xhr.onerror', {
        status: xhr.status, readyState: xhr.readyState,
        name: file.name, size: file.size,
      });
      reject(new Error(
        'Upload connection failed. Check your network and retry.',
      ));
    };
    xhr.onabort = () => {
      detachSignal();
      reject(new UploadCancelledError());
    };
    xhr.send(file);
  });

  if (signal?.aborted) throw new UploadCancelledError();

  onPhase?.('Finalising…');
  // Defensive: backend re-verifies size + content-type via HEAD here. If
  // Tigris dropped a write (e.g. silent S3 truncation) this is the point we
  // catch it — the caller sees a clean error instead of a broken slide.
  const complete = await api.post('/videos/upload/complete', {
    file_id: initiate.file_id,
    storage_key: initiate.storage_key,
    filename: file.name,
  });

  return complete;
}

/** Format bytes/sec as a human-readable string. 0 → "—". */
export function formatSpeed(bps) {
  if (!bps || bps <= 0) return '—';
  if (bps >= 1024 * 1024) return `${(bps / (1024 * 1024)).toFixed(1)} MB/s`;
  if (bps >= 1024) return `${(bps / 1024).toFixed(0)} KB/s`;
  return `${Math.round(bps)} B/s`;
}

/** Format seconds as a compact ETA. null → "—". */
export function formatEta(seconds) {
  if (seconds == null || !isFinite(seconds) || seconds < 0) return '—';
  if (seconds < 60) return `${Math.ceil(seconds)}s`;
  if (seconds < 3600) {
    const m = Math.floor(seconds / 60);
    const s = Math.ceil(seconds % 60);
    return s ? `${m}m ${s}s` : `${m}m`;
  }
  const h = Math.floor(seconds / 3600);
  const m = Math.round((seconds % 3600) / 60);
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** Format bytes for the "X.X / Y.Y MB" line. */
export function formatBytes(bytes) {
  if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 ** 3)).toFixed(2)} GB`;
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 ** 2)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${bytes} B`;
}
